import type { RoleSource } from '../../types/framework'

export const cio: RoleSource = {
  id: 'cio',
  name: 'Chief Information Officer',
  group: 'Executive',
  domains: [
    {
      name: 'Change Adoption & Digital Workplace',
      characteristic: [
        [289, "User Empathy", "Demonstrates empathy for users."],
        [290, "Change-Fatigue Awareness", "Demonstrates awareness of change fatigue."],
        [291, "User-Centred Design", "Demonstrates belief in user-centered design."],
        [292, "Learning Empathy", "Demonstrates patience with learning curves."],
        [293, "Stakeholder Communication", "Demonstrates communication sensitivity."],
        [294, "Sustainable Change Adoption", "Demonstrates focus on sustainable adoption."],
      ],
      behaviour: [
        [295, "User Involvement in users in requirements, testing and", "Involves users in requirements, testing and solution design."],
        [296, "Digital Transformation and Adoption", "Communicates technology changes in terms of user and business impact."],
        [297, "Provision of training and support to different user", "Provides training and support appropriate to different user groups."],
        [298, "Capacity and Resource Optimisation", "Monitors adoption, satisfaction and productivity after deployment."],
        [299, "Addresses accessibility and usability barriers", "Addresses accessibility and usability barriers."],
        [300, "Digital Transformation and Adoption", "Uses feedback to improve digital workplace tools and ways of working."],
      ],
    },
    {
      name: 'Cloud, Infrastructure & Platform Management',
      characteristic: [
        [301, "Technology Infrastructure Management", "Demonstrates understanding of infrastructure trade-offs."],
        [302, "Scalability awareness", "Demonstrates scalability awareness."],
        [303, "Cost consciousness", "Demonstrates cost consciousness."],
        [304, "Crisis Leadership and Resilience", "Demonstrates resilience mindset."],
        [305, "Technology lifecycle awareness", "Demonstrates technology lifecycle awareness."],
        [306, "Decision-Making and Judgement", "Demonstrates prudent vendor and platform judgment."],
      ],
      behaviour: [
        [307, "Technology Infrastructure Management", "Plans infrastructure capacity, lifecycle renewal and platform modernization."],
        [308, "Technology Infrastructure Management", "Assesses cloud and hosting options against security, cost and performance."],
        [309, "Technology Infrastructure Management", "Monitors infrastructure availability, utilization and technical vulnerabilities."],
        [310, "Technology Infrastructure Management", "Controls cloud consumption and addresses wasteful resource usage."],
        [311, "Ensuring resilient configurations, backups and recovery capabilities", "Ensures resilient configurations, backups and recovery capabilities."],
        [312, "Change Management", "Manages platform changes with testing, rollback and continuity arrangements."],
      ],
    },
    {
      name: 'Cybersecurity & Information Protection',
      characteristic: [
        [313, "Security-conscious mindset", "Demonstrates security-conscious mindset."],
        [314, "Risk awareness", "Demonstrates risk awareness."],
        [315, "Respect for privacy", "Demonstrates respect for privacy."],
        [316, "Crisis Leadership and Resilience", "Demonstrates vigilance toward emerging threats."],
        [317, "Accountability for protection of information", "Demonstrates accountability for protection of information."],
        [318, "Decision-Making and Judgement", "Demonstrates balanced security-versus-usability judgment."],
      ],
      behaviour: [
        [319, "Cybersecurity and Information Protection", "Maintains a risk-based cybersecurity program with clear accountability."],
        [320, "Implements appropriate identity, access, monitoring and incident controls", "Implements appropriate identity, access, monitoring and incident controls."],
        [321, "Ensuring vulnerabilities and security incidents are triaged and", "Ensures vulnerabilities and security incidents are triaged and remediated promptly."],
        [322, "Tests incident response and recovery arrangements", "Tests incident response and recovery arrangements."],
        [323, "Execution and Delivery", "Integrates security and privacy requirements into technology delivery."],
        [324, "Leadership Effectiveness", "Reports material cyber risks and control status to executive leadership and the board."],
      ],
    },
    {
      name: 'Data Governance, Analytics & AI',
      characteristic: [
        [325, "Data-driven orientation", "Demonstrates data-driven orientation."],
        [326, "Data Governance and Analytics", "Demonstrates respect for data quality."],
        [327, "Integrity and Ethical Leadership", "Demonstrates ethical awareness of data use."],
        [328, "Data Governance and Analytics", "Demonstrates curiosity about analytics and AI."],
        [329, "Appreciation of model limitations", "Demonstrates appreciation of model limitations."],
        [330, "Commitment to accountable data ownership", "Demonstrates commitment to accountable data ownership."],
      ],
      behaviour: [
        [331, "Establishment of data ownership, quality standards and access", "Establishes data ownership, quality standards and access rules."],
        [332, "Data Governance and Analytics", "Enables reliable data integration and decision-useful analytics."],
        [333, "Data Governance and Analytics", "Assesses AI use cases for value, accuracy, bias, privacy and risk."],
        [334, "Ensuring models and automated decisions have oversight", "Ensures models and automated decisions have appropriate oversight."],
        [335, "Regulatory Compliance and Governance", "Monitors data lineage, retention and compliance requirements."],
        [336, "Data Governance and Analytics", "Measures adoption and business impact of analytics and AI initiatives."],
      ],
    },
    {
      name: 'Digital Products & Innovation',
      characteristic: [
        [337, "Innovation and Transformation", "Demonstrates openness to experimentation."],
        [338, "Customer and User Focus", "Demonstrates customer-oriented technology mindset."],
        [339, "Curiosity about emerging technologies", "Demonstrates curiosity about emerging technologies."],
        [340, "Continuous Learning and Improvement", "Demonstrates comfort with iterative learning."],
        [341, "Commercial awareness", "Demonstrates commercial awareness."],
        [342, "Innovation and Transformation", "Demonstrates disciplined innovation judgment."],
      ],
      behaviour: [
        [343, "Identification of digital opportunities with business owners and", "Identifies digital opportunities with business owners and users."],
        [344, "Application of prototypes and pilots to test assumptions", "Uses prototypes and pilots to test assumptions before scaling."],
        [345, "Performance Management and Monitoring", "Prioritizes product roadmaps using user needs and measurable outcomes."],
        [346, "Innovation and Transformation", "Ensures innovation projects have product ownership and funding discipline."],
        [347, "Scales successful experiments while retiring ineffective solutions", "Scales successful experiments while retiring ineffective solutions."],
        [348, "Change Management", "Tracks adoption, experience and value delivered by digital products."],
      ],
    },
    {
      name: 'Enterprise Architecture & Technology Standards',
      characteristic: [
        [349, "Architectural thinking", "Demonstrates architectural thinking."],
        [350, "Preference for coherent systems", "Demonstrates preference for coherent systems."],
        [351, "Awareness of technical debt", "Demonstrates awareness of technical debt."],
        [352, "Pragmatism about standards", "Demonstrates pragmatism about standards."],
        [353, "Long-term technology perspective", "Demonstrates long-term technology perspective."],
        [354, "Appreciation of interoperability", "Demonstrates appreciation of interoperability."],
      ],
      behaviour: [
        [355, "Maintenance of enterprise architecture aligned with business capabilities", "Maintains an enterprise architecture aligned with business capabilities."],
        [356, "Setting of standards for integration, platforms, data and", "Sets standards for integration, platforms, data and technology choices."],
        [357, "Assesses architectural implications before major investments", "Assesses architectural implications before major investments."],
        [358, "Manages technical debt and reduces unnecessary complexity", "Manages technical debt and reduces unnecessary complexity."],
        [359, "Ensuring exceptions to standards have explicit rationale and", "Ensures exceptions to standards have explicit rationale and ownership."],
        [360, "Crisis Leadership and Resilience", "Reviews architecture for scalability, resilience and lifecycle sustainability."],
      ],
    },
    {
      name: 'IT Financial Management & Benefits Realization',
      characteristic: [
        [361, "Integrity and Ethical Leadership", "Demonstrates cost transparency."],
        [362, "Financial discipline", "Demonstrates financial discipline."],
        [363, "Value orientation", "Demonstrates value orientation."],
        [364, "Understanding of technology economics", "Demonstrates understanding of technology economics."],
        [365, "Decision-Making and Judgement", "Demonstrates prudent risk-return judgment."],
        [366, "Accountability for outcomes", "Demonstrates accountability for outcomes."],
      ],
      behaviour: [
        [367, "Financial Planning and Forecasting", "Maintains transparent IT budgets, forecasts and cost allocation."],
        [368, "Tracking of unit costs and major technology cost", "Tracks unit costs and major technology cost drivers."],
        [369, "Building business cases with measurable benefits and credible", "Builds business cases with measurable benefits and credible assumptions."],
        [370, "Financial Planning and Forecasting", "Monitors spend, commitments and forecast variances."],
        [371, "Execution and Delivery", "Reviews realized benefits and recurring costs after delivery."],
        [372, "Crisis Leadership and Resilience", "Balances cost optimization with resilience, security and future capability needs."],
      ],
    },
    {
      name: 'IT Governance, Compliance & Architecture Risk',
      characteristic: [
        [373, "Respect for accountability", "Demonstrates respect for accountability."],
        [374, "Control awareness", "Demonstrates control awareness."],
        [375, "Integrity and Ethical Leadership", "Demonstrates ethical judgment."],
        [376, "Appreciation of regulatory obligations", "Demonstrates appreciation of regulatory obligations."],
        [377, "Willingness to escalate", "Demonstrates willingness to escalate."],
        [378, "Balanced governance mindset", "Demonstrates balanced governance mindset."],
      ],
      behaviour: [
        [379, "Maintenance of technology decision rights and governance forums", "Maintains clear technology decision rights and governance forums."],
        [380, "Ensuring IT controls meet applicable legal, regulatory and", "Ensures IT controls meet applicable legal, regulatory and contractual requirements."],
        [381, "Tracking of technology risks, exceptions and remediation commitments", "Tracks technology risks, exceptions and remediation commitments."],
        [382, "Analytical Thinking and Evidence Use", "Maintains appropriate evidence for audits and control reviews."],
        [383, "Escalates material technology risks and unresolved control weaknesses", "Escalates material technology risks and unresolved control weaknesses."],
        [384, "Execution and Delivery", "Ensures governance is proportionate and does not unnecessarily obstruct delivery."],
      ],
    },
    {
      name: 'IT Portfolio, Investment & Vendor Management',
      characteristic: [
        [385, "Capital Allocation and Investment Appraisal", "Demonstrates investment discipline."],
        [386, "Influence and Negotiation", "Demonstrates commercial negotiation judgment."],
        [387, "Awareness of supplier dependency", "Demonstrates awareness of supplier dependency."],
        [388, "Focus on total cost of ownership", "Demonstrates focus on total cost of ownership."],
        [389, "Decision-Making and Judgement", "Demonstrates impartial procurement judgment."],
        [390, "Portfolio-Level Thinking", "Demonstrates portfolio-level thinking."],
      ],
      behaviour: [
        [391, "Maintenance of prioritized IT portfolio linked to business", "Maintains a prioritized IT portfolio linked to business benefits."],
        [392, "Execution and Delivery", "Evaluates lifecycle costs, delivery risks and alternatives for technology proposals."],
        [393, "Manages vendor performance, contracts, renewals and service commitments", "Manages vendor performance, contracts, renewals and service commitments."],
        [394, "Assesses lock-in, concentration and exit risks for critical", "Assesses lock-in, concentration and exit risks for critical providers."],
        [395, "Execution and Delivery", "Stops or redirects initiatives that fail agreed value or delivery tests."],
        [396, "Execution and Delivery", "Tracks realized benefits and total cost after implementation."],
      ],
    },
    {
      name: 'IT Service Management & Reliability',
      characteristic: [
        [397, "Service reliability orientation", "Demonstrates service reliability orientation."],
        [398, "Calmness during incidents", "Demonstrates calmness during incidents."],
        [399, "Customer and User Focus", "Demonstrates customer-service mindset."],
        [400, "Discipline in operational processes", "Demonstrates discipline in operational processes."],
        [401, "Awareness of service dependencies", "Demonstrates awareness of service dependencies."],
        [402, "Commitment to continual improvement", "Demonstrates commitment to continual improvement."],
      ],
      behaviour: [
        [403, "Definition of service levels and ownership for critical", "Defines service levels and ownership for critical technology services."],
        [404, "Monitoring of availability, performance and recurring incidents", "Monitors availability, performance and recurring incidents."],
        [405, "Communication Skills", "Coordinates incident response and communicates service impact clearly."],
        [406, "Conducts root-cause reviews and tracks preventive actions", "Conducts root-cause reviews and tracks preventive actions."],
        [407, "Tests backup, restoration and disaster recovery for critical", "Tests backup, restoration and disaster recovery for critical services."],
        [408, "Application of service data to improve reliability and", "Uses service data to improve reliability and user experience."],
      ],
    },
    {
      name: 'IT Strategy & Business Alignment',
      characteristic: [
        [409, "Business-oriented technology thinking", "Demonstrates business-oriented technology thinking."],
        [410, "Strategic Thinking and Alignment", "Demonstrates strategic foresight."],
        [411, "Understanding of digital value", "Demonstrates understanding of digital value."],
        [412, "Systems thinking", "Demonstrates systems thinking."],
        [413, "Planning and Prioritisation", "Demonstrates pragmatic prioritization."],
        [414, "Ability to translate between business and technology", "Demonstrates ability to translate between business and technology."],
      ],
      behaviour: [
        [415, "Strategic Thinking and Alignment", "Aligns the IT strategy with business strategy and operating priorities."],
        [416, "Development of technology roadmap with measurable business outcomes", "Develops a technology roadmap with measurable business outcomes."],
        [417, "Planning and Prioritisation", "Prioritizes technology investments by value, risk and feasibility."],
        [418, "Engages business leaders to clarify needs and expected", "Engages business leaders to clarify needs and expected benefits."],
        [419, "Change Management", "Updates technology priorities as business and technology conditions change."],
        [420, "Measures whether IT capabilities enable strategic objectives", "Measures whether IT capabilities enable strategic objectives."],
      ],
    },
    {
      name: 'Technology Delivery & Agile Execution',
      characteristic: [
        [421, "Execution and Delivery", "Demonstrates delivery discipline."],
        [422, "Adaptability", "Demonstrates adaptability."],
        [423, "Balanced view of speed and quality", "Demonstrates balanced view of speed and quality."],
        [424, "Collaboration and Relationship Building", "Demonstrates collaborative mindset."],
        [425, "Decision-Making and Judgement", "Demonstrates realistic estimation judgment."],
        [426, "Continuous Learning and Improvement", "Demonstrates learning orientation."],
      ],
      behaviour: [
        [427, "Execution and Delivery", "Establishes clear ownership, milestones and acceptance criteria for delivery."],
        [428, "Execution and Delivery", "Selects delivery methods suited to the work, risk and uncertainty."],
        [429, "Manages dependencies across teams and external suppliers", "Manages dependencies across teams and external suppliers."],
        [430, "Execution and Delivery", "Surfaces delivery risks and scope changes early."],
        [431, "Ensuring testing, release management and operational readiness", "Ensures testing, release management and operational readiness."],
        [432, "Execution and Delivery", "Reviews delivery outcomes and improves estimation and execution practices."],
      ],
    },
    {
      name: 'Technology Talent & Operating Model',
      characteristic: [
        [433, "Continuous Learning and Improvement", "Demonstrates commitment to technical learning."],
        [434, "Ability to build multidisciplinary teams", "Demonstrates ability to build multidisciplinary teams."],
        [435, "Openness to new skills", "Demonstrates openness to new skills."],
        [436, "Respect for specialist expertise", "Demonstrates respect for specialist expertise."],
        [437, "Leadership Effectiveness", "Demonstrates leadership adaptability."],
        [438, "Awareness of scarce technology capabilities", "Demonstrates awareness of scarce technology capabilities."],
      ],
      behaviour: [
        [439, "Planning and Prioritisation", "Plans technology workforce capabilities against the roadmap."],
        [440, "Development of leaders and specialists in technical and", "Develops leaders and specialists in relevant technical and business skills."],
        [441, "Collaboration and Relationship Building", "Builds effective collaboration among engineering, security, data and business teams."],
        [442, "Application of sourcing and partner models with accountability", "Uses sourcing and partner models with clear accountability."],
        [443, "Planning and Prioritisation", "Creates career and knowledge-transfer plans for critical roles."],
        [444, "Crisis Leadership and Resilience", "Assesses whether the IT operating model supports speed, resilience and service quality."],
      ],
    },
  ],
}
